import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  forgotPasswordSchema,
  type ForgotPasswordForm,
} from '@/lib/schemas/profile';
import { useRequestPasswordReset } from '@/lib/api/profile';

/**
 * /auth/forgot-password (AK-230) — standalone-страница без AppShell.
 *
 * Backend: `POST /auth/password-reset/request` — всегда 200 (защита от
 * email enumeration). После submit показываем success-state «если аккаунт
 * существует — письмо отправлено», без раскрытия информации.
 *
 * Сама смена пароля происходит на /auth/reset-password?token=... после
 * клика по письму.
 */
export function ForgotPasswordPage() {
  const requestMut = useRequestPasswordReset();
  const [sent, setSent] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    // AK-465 (добивка к PR #534): НЕ возвращать 'onBlur'. В mode:'onBlur'
    // валидация запускается только на blur, `isValid` остаётся false, а кнопка
    // ниже = `disabled={isSubmitting || !isValid}` → залипает disabled.
    // Здесь единственное поле, поэтому естественный сценарий «набрал email →
    // Enter» ломается полностью: blur'а нет, кнопка disabled, и браузер не
    // диспатчит implicit submission на disabled default button. 'onChange'
    // валидирует на каждом keystroke → кнопка активна сразу после валидного email.
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setServerError(null);
    try {
      await requestMut.mutateAsync({ email: data.email });
      setSent(data.email);
    } catch {
      // Backend всегда возвращает 200 — сюда попадаем только при сетевой ошибке.
      setServerError('Сетевая ошибка. Попробуйте ещё раз.');
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">
            Восстановление пароля
          </CardTitle>
          <CardDescription>
            Введите email — мы пришлём ссылку для сброса пароля
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Если аккаунт с email{' '}
                  <span className="font-medium">{sent}</span> существует, мы
                  отправили ссылку для сброса пароля. Проверьте почту (и папку
                  «Спам»).
                </AlertDescription>
              </Alert>
              <Button asChild variant="outline" className="w-full">
                <a href="/login">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Вернуться к входу
                </a>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {serverError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="forgot-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.ru"
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    className="pl-9 h-10"
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="text-destructive text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                className="w-full"
                size="lg"
              >
                {isSubmitting ? 'Отправляем…' : 'Отправить ссылку'}
              </Button>

              <div className="text-center">
                <a
                  href="/login"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Вернуться к входу
                </a>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPasswordPage;
